/**
 * Tier Configuration Constants for Community Onboarding
 * Defines tier-specific settings, benefits, and metadata
 */

import {
  Award,
  Star,
  Rocket,
  DollarSign,
  Shield,
  Zap,
  Building,
  Target,
  Users,
  Settings,
  Eye,
  Clock,
} from 'lucide-react';

export type TierType = 'silver' | 'gold' | 'custom';

export interface TierConfig {
  color: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  label: string;
}

export interface TierBenefit {
  icon: React.ComponentType<{ className?: string; size?: number }>;
  title: string;
  description: string;
}

export interface TierDetails {
  name: string;
  color: string;
  highlights: string[];
  pricing: string;
}

export const TIER_CONFIGS: Record<TierType, TierConfig> = {
  silver: {
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: Award,
    label: 'Silver Package',
  },
  gold: {
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Star,
    label: 'Gold Package',
  },
  custom: {
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: Rocket,
    label: 'Custom Solution',
  },
};

export const TIER_TITLES: Record<TierType, string> = {
  silver: 'Silver Package - Community Assessment',
  gold: 'Gold Package - Advanced Community Assessment',
  custom: 'Custom Solution - Enterprise Assessment',
};

export const TIER_SUBTITLES: Record<TierType, string> = {
  silver:
    "Let's understand your community's needs to customize your Silver package implementation and demonstrate the wealth-building features that will transform your HOA.",
  gold: "We'll assess your community's advanced requirements to tailor your Gold package with enhanced features and create a comprehensive transformation roadmap.",
  custom:
    'Help us understand your enterprise-level requirements so we can design a completely custom solution that meets your unique community vision.',
};

export const TIER_BENEFITS: Record<TierType, TierBenefit[]> = {
  silver: [
    {
      icon: DollarSign,
      title: 'Wealth Building Focus',
      description: 'Treasury optimization & group buying power',
    },
    { icon: Shield, title: 'Secure Voting', description: 'End governance disputes forever' },
    { icon: Zap, title: 'Quick Implementation', description: '6-8 week transformation timeline' },
  ],
  gold: [
    {
      icon: DollarSign,
      title: 'Generate Revenue from Community Services',
      description: 'Turn vendor work into automatic treasury funding',
    },
    {
      icon: Building,
      title: 'Advanced Group Buying',
      description: 'Better bulk purchasing & contract negotiation',
    },
    {
      icon: Target,
      title: 'Dedicated Success Team',
      description: 'Account manager & quarterly reviews',
    },
  ],
  custom: [
    { icon: Rocket, title: 'Unlimited Customization', description: 'Built exactly for your needs' },
    { icon: Users, title: 'Enterprise Support', description: 'Dedicated development resources' },
    {
      icon: Settings,
      title: 'Advanced Integrations',
      description: 'Connect with any existing system',
    },
  ],
};

export const DEFAULT_BENEFITS: TierBenefit[] = [
  { icon: Eye, title: 'Personalized Demo', description: 'Focused on your specific challenges' },
  { icon: Clock, title: 'Efficient Process', description: '45-minute targeted discussion' },
  { icon: Award, title: 'Expert Guidance', description: 'Tailored recommendations & ROI' },
];

export const TIER_DETAILS: Record<TierType, TierDetails> = {
  silver: {
    name: 'Silver Package',
    color: 'from-gray-600 to-gray-800',
    highlights: ['Wealth-building focus', 'Group buying power', 'Secure voting'],
    pricing: 'Starting at $2,500/month',
  },
  gold: {
    name: 'Gold Package',
    color: 'from-yellow-600 to-orange-600',
    highlights: ['Advanced analytics', 'Multi-community support', 'Priority support'],
    pricing: 'Starting at $4,500/month',
  },
  custom: {
    name: 'Custom Solution',
    color: 'from-purple-600 to-indigo-600',
    highlights: ['Unlimited customization', 'Enterprise support', 'Advanced integrations'],
    pricing: 'Contact for pricing',
  },
};
